/**
 * Verdict bottom-sheet card — web.
 * Given a Verdict, render a Green/Amber/Red decision with reasons,
 * a confidence bar, and source count.
 *
 * No framework, no CSS pipeline — everything inline so the core loop
 * cannot be broken by build config.
 */

import type { Verdict, VerdictLevel } from "../data/verdicts";
import {
  LEVEL_COLORS,
  LEVEL_TAILS,
  MODE_ACCENT,
  DUR,
  EASE,
  Z,
  BP_MOBILE,
} from "./tokens";
import { relativeAge } from "./freshness";
import { pickPriceMedian } from "./budget-format";
import { reviewStampLine } from "./compare-card";
import {
  getMode,
  getModeLabel,
  subscribe,
  type PersonaMode,
} from "./decision-lens";
import { interpretForMode } from "./single-area-lens";
import {
  getPriority,
  getPriorityLabel,
  getPriorityAccent,
  subscribePriority,
} from "./constraint-priority";
import {
  getBudgetBand,
  getBudgetBandLabel,
  getBudgetBandAccent,
  subscribeBudgetBand,
  subscribeBudgetBucket,
} from "./budget-band";
import { classifyFit, fitDecisionAnchor, fitLabel } from "./budget-school-fit";
import {
  constrainBestFor,
  constrainTradeoff,
  reorderReasonsByPriority,
} from "./constraint-layer";
import { getPurchaseMode } from "./purchase-mode";
import { getPropertyType } from "./property-type";
import {
  practicalPriceLabel,
  practicalPriceContext,
  deriveCityFromVerdict,
} from "./why-this-beats-that";
import {
  getCompareState,
  compareFromSurface,
  subscribeCompare,
} from "./compare-state";
import { getActiveDataset } from "./active-dataset";
import { buildTruthBlock } from "./truth-block";
import { guideUrlFor } from "./guide-link";
import {
  getSchoolRanks,
  hasSchoolRank,
  trendLabel,
  kindLabel,
  levelLabel,
  SCHOOL_RANK_UNKNOWN_LINE,
} from "../data/school-ranks";
// School-density signal — count-based descriptor derived purely from
// SCHOOL_RANKS. We surface it here as (a) a one-line "School presence:
// __" row beneath the reason bullets, and (b) a bullet appended to the
// reasons list. No score change; verdict colour is untouched.
import {
  getSchoolDensity,
  densityLabel,
  densityHint,
  densityReasonBullet,
  coverageNote,
} from "../data/school-density";
// Safety signal — categorical (Low / Moderate / High incident activity)
// derived from the existing incident feed. Replaces the old numeric
// "Safety: 10.0" that collapsed to 10 anywhere with zero local items.
// `safetyCountLine` composes "X incidents (from Y reports across
// sources)" so the user understands when a count was corroborated by
// multiple publishers; `safetyMergedHint` adds "Multiple reports of
// same event merged" only when dedup actually collapsed something.
// Tradeoff detector combines verdict + density + safety to flag the
// rosier-than-it-seems and harsher-than-it-seems contradictions.
import {
  getSafety,
  safetyActivityLabel,
  safetyHint,
  safetyReasonBullet,
  safetyCoverageNote,
  safetyCountLine,
  safetyMergedHint,
  safetyDataConfidence,
} from "../data/safety-signal";
import { getTradeoff } from "../data/tradeoff";
import {
  proximitySummary,
  nearestTopSchools,
} from "./hyd-school-proximity";

/** Pill ink per verdict — contrast companions for the token triad. The
 *  background colours themselves come from tokens.LEVEL_COLORS; the tail
 *  copy from tokens.LEVEL_TAILS. Same verdict, same colour, everywhere. */
const LEVEL_FG: Record<VerdictLevel, string> = {
  green: "#eafff5",
  amber: "#1a1200",
  red: "#fff0f0",
};

function levelTheme(level: VerdictLevel): {
  bg: string;
  fg: string;
  label: string;
} {
  return {
    bg: LEVEL_COLORS[level],
    fg: LEVEL_FG[level],
    label: `${level.toUpperCase()} — ${LEVEL_TAILS[level]}`,
  };
}

/**
 * Per-mode accent colours. Makes the mode change visually obvious on sight
 * (not just a two-line text swap) — badge, "Best for" highlight, and the
 * card's top strip all shift colour with the lens. Badge hues come from
 * tokens.MODE_ACCENT (the "bachelor" persona renders with the renter
 * accent); the badge-ink and "Best for" highlight are contrast companions.
 */
const PERSONA_ACCENT: Record<PersonaMode, { badge: string; badgeFg: string; bestFor: string }> = {
  family:   { badge: MODE_ACCENT.family,   badgeFg: "#0b1a0e", bestFor: "#9BE3A7" },
  bachelor: { badge: MODE_ACCENT.renter,   badgeFg: "#0b1420", bestFor: "#AFD5F5" },
  investor: { badge: MODE_ACCENT.investor, badgeFg: "#1a1200", bestFor: "#FFE4A6" },
};

const CARD_ID = "hyperlocal-verdict-card";

/* -------------------------------------------------------------------------- */
/*  Mobile stylesheet — idempotent injection.                                 */
/*  On small viewports the 460px centered card sits on top of the shortlist   */
/*  bottom-sheet and the top pills. Convert it to a full-width bottom-sheet   */
/*  that scrolls internally, respecting safe-area.                            */
/* -------------------------------------------------------------------------- */
function ensureVerdictMobileStyles(): void {
  if (document.getElementById("hyperlocal-verdict-mq")) return;
  const s = document.createElement("style");
  s.id = "hyperlocal-verdict-mq";
  s.textContent = `
    @media (max-width: 720px), (max-height: 500px) {
      #${CARD_ID} {
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        top: auto !important;
        transform: none !important;
        width: 100% !important;
        max-width: 100% !important;
        /* iOS Safari/Chrome measure vh against the largest viewport (URL bar
         * hidden), so 68vh overflows the visible area when the URL bar is
         * showing. dvh tracks the *dynamic* viewport and shrinks with the
         * URL bar, keeping the card inside the frame. The calc() guarantees
         * at least 96px of clearance above the card so the top pills and
         * the search bar stay reachable. vh is kept as a fallback for
         * browsers that predate dvh (iOS < 15.4, Android Chrome < 108). */
        max-height: 68vh !important;
        max-height: min(68dvh, calc(100dvh - 96px)) !important;
        overflow-y: auto !important;
        overscroll-behavior: contain !important;
        -webkit-overflow-scrolling: touch !important;
        border-radius: var(--r-card) var(--r-card) 0 0 !important;
        padding: 14px 16px calc(16px + env(safe-area-inset-bottom, 0px)) !important;
        z-index: ${Z.sheet} !important;
      }
      #${CARD_ID}::before {
        content: "";
        display: block;
        width: 36px;
        height: 4px;
        background: var(--gold-a35);
        border-radius: 999px;
        margin: -6px auto 10px;
      }
      @keyframes hyperlocalVerdictIn {
        from { opacity: 0; transform: translateY(12px); }
        to   { opacity: 1; transform: translateY(0); }
      }
    }
  `;
  document.head.appendChild(s);
}

/** Handle for the active card's mode-change subscription, so we can release
 * it the instant the card is dismissed. */
let activeUnsubscribe: (() => void) | null = null;

/** Handle for the active card's priority-change subscription. Released on the
 * same lifecycle as the mode subscription — both must let go on dismiss so the
 * module doesn't accumulate listeners for closed cards. */
let activePriorityUnsubscribe: (() => void) | null = null;

/** Handle for the active card's budget-band subscription — parallel to the
 * priority subscription. Flipping the band while a card is open re-renders
 * it so the Good-fit/Stretch/Weak-fit copy updates in place. */
let activeBandUnsubscribe: (() => void) | null = null;
/** Released alongside the band subscription — fires when the user picks
 *  a different bucket in the Decision-lens dropdown without changing the
 *  band (e.g. A$600–700k → A$700–800k, both "tight"). Needed because
 *  `setBudgetBand` no-ops when the band is unchanged, so only the
 *  bucket-level signal catches within-band moves. */
let activeBucketUnsubscribe: (() => void) | null = null;
/** Unsubscribes the compare-state listener that keeps the popup's Compare
 *  button label in sync. */
let activeCompareUnsubscribe: (() => void) | null = null;

/**
 * Release every active subscription in one call. Used by the in-card
 * listeners before they rebuild, and by `dismissVerdict`. Keeping the
 * unsubscribe logic in one place stops the "forgot to release axis X"
 * class of leak as new constraint axes get added later.
 */
function releaseSubscriptions(): void {
  if (activeUnsubscribe) {
    activeUnsubscribe();
    activeUnsubscribe = null;
  }
  if (activePriorityUnsubscribe) {
    activePriorityUnsubscribe();
    activePriorityUnsubscribe = null;
  }
  if (activeBandUnsubscribe) {
    activeBandUnsubscribe();
    activeBandUnsubscribe = null;
  }
  if (activeBucketUnsubscribe) {
    activeBucketUnsubscribe();
    activeBucketUnsubscribe = null;
  }
  if (activeCompareUnsubscribe) {
    activeCompareUnsubscribe();
    activeCompareUnsubscribe = null;
  }
}

/* -------------------------------------------------------------------------- */
/*  Visibility subscribers — used by the map to draw/clear the selected-      */
/*  suburb ring, and potentially by future surfaces that care.                */
/* -------------------------------------------------------------------------- */
const verdictListeners = new Set<(v: Verdict | null) => void>();

export function subscribeVerdictCard(
  fn: (v: Verdict | null) => void
): () => void {
  verdictListeners.add(fn);
  return () => {
    verdictListeners.delete(fn);
  };
}

function notifyVerdictCard(v: Verdict | null): void {
  for (const fn of Array.from(verdictListeners)) {
    try {
      fn(v);
    } catch {
      /* broken subscriber must not break the rest */
    }
  }
}

export function dismissVerdict(): void {
  const existing = document.getElementById(CARD_ID);
  if (existing) existing.remove();
  releaseSubscriptions();
  notifyVerdictCard(null);
}

export function showVerdict(v: Verdict): void {
  dismissVerdict();
  ensureVerdictMobileStyles();
  notifyVerdictCard(v);

  // Broadcast to the right-rail Suburb Intelligence panel so it switches
  // to the suburb the user just tapped. Added 2026-05-07 as part of the
  // SimCity sprint — the panel listens for hyperlocal:verdict-shown.
  try {
    document.dispatchEvent(
      new CustomEvent("hyperlocal:verdict-shown", {
        detail: { verdict: v },
        bubbles: true,
      }),
    );
  } catch {
    /* DOM dispatch failed — non-fatal */
  }

  const theme = levelTheme(v.verdict);
  const mode = getMode();
  // In Exploration mode (lens=none) the card skips its persona block
  // entirely — but accent colours are still used for the top border and
  // the mode badge's visuals. Fall back to a neutral family-style accent
  // so the popup still has a coherent visual identity.
  const personaForAccent: PersonaMode = mode === "none" ? "family" : mode;
  const accent = PERSONA_ACCENT[personaForAccent];
  const priority = getPriority();
  const priorityAccent = getPriorityAccent(priority);
  // Budget × School intersection axis — the second of the two constraints
  // users actually decide on. Only engages when Priority === "school".
  const band = getBudgetBand();
  const bandAccent = getBudgetBandAccent(band);
  const fit = priority === "school" ? classifyFit(v, band) : null;

  const card = document.createElement("div");
  card.id = CARD_ID;
  card.setAttribute("role", "dialog");
  card.setAttribute("aria-label", `Verdict for ${v.name}`);
  // Right-rail anchor (replaces the retired Suburb Intelligence panel).
  // top:96 matches the area snapshot's anchor so the snapshot reads as
  // expanding into this card — the two surfaces share one slot. The card
  // is the canonical suburb info surface on desktop; the mobile override
  // below collapses it back into a bottom-sheet so small viewports stay
  // legible. (124px clearance = 96 top + ~28 bottom breathing room.)
  card.style.cssText = `
    position: fixed;
    right: 14px;
    top: 96px;
    width: min(40vw, 460px);
    max-height: calc(100dvh - 124px);
    overflow-y: auto;
    background: var(--surface);
    color: #f1f1f1;
    border-radius: var(--r-card);
    padding: 18px 20px 16px 20px;
    box-shadow: 0 20px 50px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06);
    font: 14px/1.45 system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
    z-index: ${Z.card};
    animation: hyperlocalVerdictIn ${DUR.base}ms ${EASE.spring};
    border-top: 3px solid ${accent.badge};
  `;

  if (!document.getElementById("hyperlocal-verdict-anim")) {
    const style = document.createElement("style");
    style.id = "hyperlocal-verdict-anim";
    style.textContent = `
      @keyframes hyperlocalVerdictIn {
        from { opacity: 0; transform: translateX(16px); }
        to   { opacity: 1; transform: translateX(0); }
      }
      @media (prefers-reduced-motion: reduce) {
        #${CARD_ID} { animation: none !important; }
      }
    `;
    document.head.appendChild(style);
  }

  // Header: G/A/R pill + mode badge + close. Mode badge tells the user which
  // lens is driving the copy they're about to read, and its colour + label
  // flips live when the global lens is changed.
  const header = document.createElement("div");
  header.style.cssText =
    "display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:10px;flex-wrap:wrap;";

  const pillGroup = document.createElement("div");
  pillGroup.style.cssText =
    "display:flex;align-items:center;gap:6px;flex-wrap:wrap;";

  const pill = document.createElement("span");
  pill.textContent = theme.label;
  pill.style.cssText = `
    background: ${theme.bg};
    color: ${theme.fg};
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.02em;
    padding: 5px 10px;
    border-radius: 999px;
    text-transform: uppercase;
  `;

  const modeBadge = document.createElement("span");
  modeBadge.textContent = `Lens · ${getModeLabel(mode)}`;
  modeBadge.style.cssText = `
    background: ${accent.badge};
    color: ${accent.badgeFg};
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.04em;
    padding: 4px 9px;
    border-radius: 999px;
    text-transform: uppercase;
  `;

  pillGroup.appendChild(pill);
  pillGroup.appendChild(modeBadge);

  // Priority badge — only shown when an active constraint is selected. Sits
  // next to the lens badge so the user sees both context dimensions (who they
  // are + what's non-negotiable today) at a glance.
  if (priority !== "none") {
    const priorityBadge = document.createElement("span");
    priorityBadge.textContent = `Priority · ${getPriorityLabel(priority)}`;
    priorityBadge.style.cssText = `
      background: ${priorityAccent.badge};
      color: ${priorityAccent.badgeFg};
      font-size: 10.5px;
      font-weight: 700;
      letter-spacing: 0.04em;
      padding: 4px 9px;
      border-radius: 999px;
      text-transform: uppercase;
    `;
    pillGroup.appendChild(priorityBadge);
  }

  // Fit chip — Budget × School intersection. Rendered in-line with the
  // existing badges so the classification (Good fit / Stretch / Weak fit)
  // sits next to its context, not in a new block. Only shown when Priority
  // === "school" AND a budget band is set; invisible otherwise so the
  // default UI stays compact.
  if (fit) {
    const fitChip = document.createElement("span");
    fitChip.textContent = `${fitLabel(fit)} · ${getBudgetBandLabel(band)}`;
    fitChip.style.cssText = `
      background: ${bandAccent.badge};
      color: ${bandAccent.badgeFg};
      font-size: 10.5px;
      font-weight: 700;
      letter-spacing: 0.04em;
      padding: 4px 9px;
      border-radius: 999px;
      text-transform: uppercase;
    `;
    pillGroup.appendChild(fitChip);
  }

  const close = document.createElement("button");
  close.setAttribute("aria-label", "Close verdict");
  close.textContent = "×";
  // Absolute-positioned against the card (card is position:fixed → its own
  // positioning context). Anchored to the card's top-right so the X always
  // sits on the card's top edge regardless of how many chips are rendered
  // or whether they wrap to a second line. z-index:3 keeps it above the
  // header's flex-wrap chips and the lens block below.
  close.style.cssText = `
    position: sticky;
    top: 0;
    float: right;
    margin: -8px -10px 0 0;
    z-index: 3;
    background: var(--surface-glass);
    backdrop-filter: blur(4px);
    border: 0;
    color: #888;
    font-size: 22px;
    line-height: 1;
    cursor: pointer;
    padding: 4px 10px;
    border-radius: 6px;
  `;
  close.addEventListener("click", dismissVerdict);
  close.addEventListener("mouseenter", () => (close.style.color = "#fff"));
  close.addEventListener("mouseleave", () => (close.style.color = "#888"));
  // 44px-class touch target on phones (the mouse-sized 33×30 X is the
  // hardest control on the sheet to hit one-handed). One shared rule.
  if (!document.getElementById("hyperlocal-vc-close-touch")) {
    const s = document.createElement("style");
    s.id = "hyperlocal-vc-close-touch";
    s.textContent = `
      @media ${BP_MOBILE} {
        #hyperlocal-verdict-card button[aria-label="Close verdict"] {
          padding: 10px 14px !important;
          font-size: 24px !important;
          margin: -10px -12px 0 0 !important;
        }
      }
    `;
    document.head.appendChild(s);
  }

  // Reserve space on the right of the header row so wrapped chips never flow
  // under the absolute-positioned close X. Only a padding hint; no layout
  // change.
  header.style.paddingRight = "32px";
  header.appendChild(pillGroup);

  // Title
  const title = document.createElement("div");
  title.textContent = v.name;
  title.style.cssText =
    "font-size:18px;font-weight:600;margin-bottom:10px;color:#fff;";

  // Reasons — reordered so the reason that best matches the active lens
  // surfaces first. In Exploration mode (lens=none) reasons stay in
  // their original order (no persona bias). When a constraint Priority
  // is active, priority-relevant reasons take precedence over lens-
  // relevant ones.
  const reasons = document.createElement("ul");
  reasons.style.cssText = "list-style:none;padding:0;margin:0 0 12px 0;";
  const lensOrdered =
    mode === "none" ? (v.reasons || []).slice() : reorderReasonsForMode(v, mode);
  const ordered =
    priority === "none"
      ? lensOrdered
      : reorderReasonsByPriority(
          [lensOrdered[0], lensOrdered[1], lensOrdered[2]] as [string, string, string],
          priority
        );
  for (const r of ordered) {
    const li = document.createElement("li");
    li.style.cssText =
      "padding:4px 0 4px 16px;position:relative;color:#d6d6d6;";
    const dot = document.createElement("span");
    dot.style.cssText = `
      position:absolute;left:0;top:11px;
      width:6px;height:6px;border-radius:50%;
      background:${theme.bg};
    `;
    li.appendChild(dot);
    li.appendChild(document.createTextNode(r));
    reasons.appendChild(li);
  }

  // School-density bullet — appended AFTER the canonical 3 reasons so
  // the user sees their persona-relevant reasons first, then the density
  // descriptor. A muted dot (not the verdict colour) signals that this
  // bullet is a derived signal, not part of the original verdict copy.
  // Verdict colour and confidenceScore are NOT recomputed — see
  // src/data/school-density.ts header for the scope contract.
  const _density = getSchoolDensity(v.id);
  const densityLi = document.createElement("li");
  densityLi.style.cssText =
    "padding:4px 0 4px 16px;position:relative;color:#bdbdbd;";
  const densityDot = document.createElement("span");
  densityDot.style.cssText = `
    position:absolute;left:0;top:11px;
    width:6px;height:6px;border-radius:50%;
    background:#7a8a98;
  `;
  densityLi.appendChild(densityDot);
  densityLi.appendChild(
    document.createTextNode(densityReasonBullet(_density.level))
  );
  reasons.appendChild(densityLi);

  // Safety bullet — same pattern as density. Suppressed when the
  // incident feed hasn't loaded yet (`ready === false`) so we don't
  // claim "Low incident activity" as a positive when we actually mean
  // "we don't know". The presence row below will say "(checking…)" in
  // that branch.
  const _city = deriveCityFromVerdict(v);
  const _safety = getSafety(v.id, _city);
  if (_safety.ready) {
    const safetyLi = document.createElement("li");
    safetyLi.style.cssText =
      "padding:4px 0 4px 16px;position:relative;color:#bdbdbd;";
    const safetyDot = document.createElement("span");
    safetyDot.style.cssText = `
      position:absolute;left:0;top:11px;
      width:6px;height:6px;border-radius:50%;
      background:#7a8a98;
    `;
    safetyLi.appendChild(safetyDot);
    safetyLi.appendChild(
      document.createTextNode(safetyReasonBullet(_safety.level))
    );
    reasons.appendChild(safetyLi);
  }

  // "School presence: ___" metadata row — sits BELOW the reasons list,
  // not inside it. Uses the same muted info colour as the bullet so the
  // user reads them as one block. Coverage caveat in subdued small text
  // because LOW frequently means "not in our dataset" rather than
  // "no schools here".
  const presence = document.createElement("div");
  presence.style.cssText =
    "margin:0 0 12px 0;padding:8px 10px;border-radius:8px;background:rgba(255,255,255,0.03);";
  const presenceMain = document.createElement("div");
  presenceMain.style.cssText =
    "font-size:12px;color:#d6d6d6;line-height:1.4;";
  const presenceLabel = document.createElement("strong");
  presenceLabel.style.cssText = "color:#f1f1f1;font-weight:600;";
  presenceLabel.textContent = `School presence: ${densityLabel(_density.level)}`;
  presenceMain.appendChild(presenceLabel);
  presenceMain.appendChild(
    document.createTextNode(` — ${densityHint(_density.level)}`)
  );
  const presenceCount = document.createElement("div");
  presenceCount.style.cssText =
    "font-size:11px;color:#8a8a8a;margin-top:3px;";
  presenceCount.textContent = `${_density.count} school${_density.count === 1 ? "" : "s"} on file. ${coverageNote()}`;
  presence.appendChild(presenceMain);
  presence.appendChild(presenceCount);

  // Safety presence row — same visual treatment as School presence so
  // the two derived signals read as a paired block of "what's nearby".
  // Falls back to a "(checking…)" state when the incident feed isn't
  // loaded yet — never claims SAFE in that branch.
  const safetyBox = document.createElement("div");
  safetyBox.style.cssText =
    "margin:0 0 12px 0;padding:8px 10px;border-radius:8px;background:rgba(255,255,255,0.03);";
  const safetyMain = document.createElement("div");
  safetyMain.style.cssText =
    "font-size:12px;color:#d6d6d6;line-height:1.4;";
  const safetyLabelStrong = document.createElement("strong");
  safetyLabelStrong.style.cssText = "color:#f1f1f1;font-weight:600;";
  if (_safety.ready) {
    safetyLabelStrong.textContent = `Safety: ${safetyActivityLabel(_safety.level)} incident activity`;
    safetyMain.appendChild(safetyLabelStrong);
    safetyMain.appendChild(
      document.createTextNode(` — ${safetyHint(_safety.level)}`)
    );
  } else {
    safetyLabelStrong.textContent = `Safety: (checking…)`;
    safetyMain.appendChild(safetyLabelStrong);
    safetyMain.appendChild(
      document.createTextNode(" — incident feed loading; reopen the card in a moment")
    );
  }
  const safetyCount = document.createElement("div");
  safetyCount.style.cssText =
    "font-size:11px;color:#8a8a8a;margin-top:3px;";
  // Compose "X incidents within 1 km (reported by Y sources)" when
  // dedup pulled multiple distinct sources onto the same event. When
  // sourceCount equals count (one source per event, nothing merged)
  // the parenthetical is omitted — no need to advertise dedup that
  // didn't change anything.
  safetyCount.textContent = _safety.ready
    ? `${safetyCountLine({
        count: _safety.count,
        sourceCount: _safety.mergedSources.length,
      })}. ${safetyCoverageNote()}`
    : safetyCoverageNote();
  safetyBox.appendChild(safetyMain);
  safetyBox.appendChild(safetyCount);

  // Graded data-confidence row. Pure count→text mapping (see
  // `safetyDataConfidence` in safety-signal.ts). Tells the reader how
  // much weight to give the safety signal — separate from what the
  // signal says — based purely on how many recent reports backed it.
  // Always renders when ready so the user sees the same interpretive
  // banner regardless of suburb (zero-count suburbs get the "Not enough
  // recent data" tier, which is the honest answer).
  if (_safety.ready) {
    const safetyConfidence = document.createElement("div");
    safetyConfidence.style.cssText =
      "font-size:11px;color:#9aa5b1;margin-top:3px;";
    safetyConfidence.textContent = safetyDataConfidence(_safety.count);
    safetyBox.appendChild(safetyConfidence);
  }

  // Optional one-line hint when at least one event in this 1 km window
  // was corroborated by multiple sources. Renders only when the dedup
  // step actually merged something — otherwise the row is suppressed
  // entirely so the card doesn't bloat with constant footer copy.
  if (_safety.ready && _safety.hasMergedReports) {
    const safetyMerged = document.createElement("div");
    safetyMerged.style.cssText =
      "font-size:10.5px;color:#7a8a98;margin-top:2px;font-style:italic;";
    safetyMerged.textContent = safetyMergedHint(true);
    safetyBox.appendChild(safetyMerged);
  }

  // Tradeoff row — surfaced ONLY when the verdict + density + safety
  // combination contradicts itself in one of the brief's three cases.
  // No tradeoff → no row → no clutter.
  const _tradeoff = getTradeoff({ verdict: v, city: _city });
  let tradeoffBox: HTMLElement | null = null;
  if (_tradeoff) {
    tradeoffBox = document.createElement("div");
    // Soft amber accent (NOT the verdict-amber colour token, that's
    // reserved for the headline verdict). This sits visually between
    // "info" and "warning" so users register it without alarm.
    tradeoffBox.style.cssText =
      "margin:0 0 12px 0;padding:8px 10px;border-radius:8px;" +
      "background:rgba(255,179,71,0.08);" +
      "border-left:3px solid #FFB347;";
    const tradeoffMain = document.createElement("div");
    tradeoffMain.style.cssText =
      "font-size:12px;color:#e8d8b8;line-height:1.4;";
    const tradeoffStrong = document.createElement("strong");
    tradeoffStrong.style.cssText = "color:#FFD37E;font-weight:600;";
    tradeoffStrong.textContent = "⚠ Tradeoff detected";
    tradeoffMain.appendChild(tradeoffStrong);
    tradeoffMain.appendChild(
      document.createTextNode(` — ${_tradeoff.reason}`)
    );
    tradeoffBox.appendChild(tradeoffMain);
  }

  // Mode-aware lens block — rendered only when the user has committed
  // to a persona (lens !== "none"). In Exploration mode the popup
  // shows just the verdict + reasons + schools + price, leaving the
  // persona framing for when the user actually adopts a lens.
  let lensWrap: HTMLElement | null = null;
  if (mode !== "none") {
    const personaMode: PersonaMode = mode;
    const lensInterp = interpretForMode(v, personaMode);
    const interp = {
      bestFor: constrainBestFor(v, personaMode, priority, lensInterp.bestFor, band),
      tradeoff: constrainTradeoff(v, personaMode, priority, lensInterp.tradeoff, band),
    };
    // Side accent strip switches to the priority colour when active so
    // the user's eye sees "this read respects my priority" before the
    // copy. Budget × School takes the band colour.
    lensWrap = document.createElement("div");
    const lensWrapAccent = fit
      ? bandAccent.badge
      : priority === "none"
      ? accent.badge
      : priorityAccent.badge;
    lensWrap.style.cssText = `
      margin:0 0 10px 0;padding:10px 12px;border-radius:10px;
      background:rgba(255,255,255,0.03);
      border-left:3px solid ${lensWrapAccent};
    `;
    const bestLine = document.createElement("div");
    bestLine.style.cssText =
      "font-size:13px;font-weight:500;color:#e8e8e8;line-height:1.45;margin-bottom:3px;";
    bestLine.innerHTML = `<span style="color:${accent.bestFor};font-weight:700;">Best for:</span> ${interp.bestFor}.`;
    const tradeLine = document.createElement("div");
    tradeLine.style.cssText =
      "font-size:13px;font-weight:500;color:#c4c4c4;line-height:1.45;margin-bottom:6px;";
    tradeLine.innerHTML = `<span style="color:#9aa0a6;font-weight:700;">Tradeoff:</span> ${interp.tradeoff}.`;
    const decisionLine = document.createElement("div");
    decisionLine.style.cssText = `
      font-size:13px;font-weight:600;color:#fff;line-height:1.45;
      padding-top:6px;border-top:1px dashed rgba(255,255,255,0.10);
    `;
    if (fit) {
      const anchorLead = "If you had to choose today";
      const anchorBody = fitDecisionAnchor(fit);
      decisionLine.innerHTML = `<span style="color:${bandAccent.badge};font-weight:700;">${anchorLead},</span> ${anchorBody}.`;
    } else {
      decisionLine.innerHTML = decisionAnchorHTML(v, personaMode, accent.badge);
    }
    lensWrap.appendChild(bestLine);
    lensWrap.appendChild(tradeLine);
    lensWrap.appendChild(decisionLine);
  }

  // Freshness chip — dated from the data (pricing calibration stamp +
  // researched-median year), never the wall clock. See reviewStampLine.
  const fresh = document.createElement("div");
  fresh.style.cssText =
    "font-size:11px;color:#8a8a8a;margin:0 0 10px 0;letter-spacing:0.01em;";
  fresh.textContent = reviewStampLine(v);

  // Confidence widget REMOVED.
  //
  // Earlier versions rendered a 0–100 confidence bar plus a "High / Medium /
  // Lower" label with a one-phrase qualifier directly under the freshness
  // line. The number conflicted with the verdict colour: a card could be
  // green (act on it) and simultaneously read "67/100" (Medium — hedge),
  // and users found the dual signal untrustworthy. We removed the entire
  // widget rather than re-tune the thresholds because the verdict colour is
  // already the calibrated decision signal — restating it as a separate
  // number is what created the conflict in the first place.
  //
  // The data field `Verdict.confidenceScore` is intentionally left intact
  // on the schema so any downstream tooling that still consumes it keeps
  // working; the UI just no longer surfaces it.
  // Footer: price + source count
  const footer = document.createElement("div");
  footer.style.cssText = `
    display:flex;align-items:center;justify-content:space-between;gap:12px;
    padding-top:10px;border-top:1px solid rgba(255,255,255,0.08);
    font-size:13px;color:#bbb;
  `;

  // Practical price block — same model as the shortlist pill. No "Rough
  // price", no per-sqm units. Label reflects Buy/Rent + property type; the
  // value is a total-price or weekly-rent range; the subtitle says what
  // that money usually buys ("Typical 3-bed family house").
  const priceBlock = document.createElement("div");
  priceBlock.style.cssText = "display:flex;flex-direction:column;gap:2px;";

  const priceCtx = {
    city: deriveCityFromVerdict(v),
    purchaseMode: getPurchaseMode(),
    propertyType: getPropertyType(),
  };
  const fullLabel = practicalPriceLabel(v, priceCtx); // e.g. "Buy · House: A$900k–1.5M"
  const colonIdx = fullLabel.indexOf(":");
  const labelText = colonIdx >= 0 ? fullLabel.slice(0, colonIdx) : "Price";
  const valueText = colonIdx >= 0 ? fullLabel.slice(colonIdx + 1).trim() : fullLabel;

  const priceLabel = document.createElement("span");
  priceLabel.textContent = labelText;
  priceLabel.style.cssText = "font-size:11px;color:#888;text-transform:uppercase;letter-spacing:0.04em;";
  const priceValue = document.createElement("span");
  priceValue.textContent = valueText;
  priceValue.style.cssText = "color:#f1f1f1;font-weight:600;";
  const priceSub = document.createElement("span");
  priceSub.textContent = practicalPriceContext(v, priceCtx);
  priceSub.style.cssText = "font-size:10.5px;color:#9A9A9A;line-height:1.2;";
  priceBlock.appendChild(priceLabel);
  priceBlock.appendChild(priceValue);
  priceBlock.appendChild(priceSub);

  // Provenance disclosure — researched median vs band estimate. The same
  // `pickPriceMedian` precedence that `clampedPriceRange` uses decides
  // which path priceBadgeFor rendered, so this line always matches the
  // figure above it: a researched median credits its source + snapshot
  // year; a band fallback says "estimated" plainly. Disclosure only — no
  // ranking or formatting change.
  const researchedMedian = v.priceData
    ? pickPriceMedian(
        { city: priceCtx.city, mode: priceCtx.purchaseMode, type: priceCtx.propertyType },
        v.priceData
      )
    : null;
  const priceProvenance = document.createElement("span");
  priceProvenance.style.cssText =
    "font-size:10px;color:var(--ink-3);line-height:1.3;letter-spacing:0.01em;";
  priceProvenance.textContent =
    researchedMedian != null && v.priceData
      ? priceProvenanceLine(v.priceData.source, v.priceData.year)
      : "typical range · estimated";
  priceBlock.appendChild(priceProvenance);

  const sourcesBlock = document.createElement("div");
  sourcesBlock.style.cssText = "display:flex;flex-direction:column;gap:2px;align-items:flex-end;";
  const srcLabel = document.createElement("span");
  srcLabel.textContent = "Sources";
  srcLabel.style.cssText = "font-size:11px;color:#888;text-transform:uppercase;letter-spacing:0.04em;";
  const srcValue = document.createElement("span");
  const srcCount = Array.isArray(v.sources) ? v.sources.length : 0;
  if (srcCount === 0) {
    // Honest state, not a dash: a verdict with no named source is an
    // editorial read. "Not yet listed" is true for every city (the live
    // engine indexes Sydney; the others aren't engine-fed yet) — unlike
    // "being indexed", which would imply an active per-city process that
    // doesn't exist outside Sydney. Muted, non-interactive.
    srcValue.textContent = "Editorial read — sources not yet listed";
    srcValue.style.cssText =
      "font-size:10.5px;color:var(--ink-3);font-weight:500;line-height:1.35;max-width:200px;text-align:right;";
  } else {
    srcValue.textContent = `${srcCount} signal${srcCount === 1 ? "" : "s"}`;
    srcValue.style.cssText = "color:#f1f1f1;font-weight:600;cursor:pointer;";
  }
  sourcesBlock.appendChild(srcLabel);
  sourcesBlock.appendChild(srcValue);
  // Sources are proof, not fine print: clicking the count expands the
  // actual list — labels always, deep links when a source carries a URL.
  // Collapsed by default so the footer stays calm.
  if (srcCount > 0) {
    const srcList = document.createElement("div");
    srcList.style.cssText =
      "display:none;flex-direction:column;gap:3px;align-items:flex-end;max-width:230px;text-align:right;";
    for (const s of v.sources) {
      if (s.url) {
        const a = document.createElement("a");
        a.href = s.url;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        a.textContent = s.label;
        a.style.cssText =
          "font-size:10.5px;color:#9FCFE0;text-decoration:none;line-height:1.35;";
        a.addEventListener("mouseenter", () => (a.style.textDecoration = "underline"));
        a.addEventListener("mouseleave", () => (a.style.textDecoration = "none"));
        srcList.appendChild(a);
      } else {
        const span = document.createElement("span");
        span.textContent = s.label;
        span.style.cssText = "font-size:10.5px;color:#9A9A9A;line-height:1.35;";
        srcList.appendChild(span);
      }
    }
    const toggle = () => {
      const open = srcList.style.display !== "none";
      srcList.style.display = open ? "none" : "flex";
      srcValue.setAttribute("aria-expanded", String(!open));
    };
    srcValue.setAttribute("role", "button");
    srcValue.setAttribute("tabindex", "0");
    srcValue.setAttribute("aria-expanded", "false");
    srcValue.setAttribute("aria-label", "Show source list");
    srcValue.addEventListener("click", toggle);
    srcValue.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        toggle();
      }
    });
    sourcesBlock.appendChild(srcList);
  }

  footer.appendChild(priceBlock);
  footer.appendChild(sourcesBlock);

  // Deep guide link — every suburb has a generated guide page built from
  // this same verdict data (prices, schools, FAQs, structured data). The
  // map answers in 30 seconds; the guide carries the depth. One quiet row.
  const guideRow = document.createElement("a");
  guideRow.href = guideUrlFor(v.id, deriveCityFromVerdict(v));
  guideRow.textContent = `Read the full ${v.name} guide →`;
  guideRow.style.cssText = `
    display:block;margin-top:8px;padding-top:8px;
    border-top:1px solid rgba(255,255,255,0.08);
    font-size:11.5px;color:#9FCFE0;text-decoration:none;`;
  guideRow.addEventListener("mouseenter", () => (guideRow.style.textDecoration = "underline"));
  guideRow.addEventListener("mouseleave", () => (guideRow.style.textDecoration = "none"));

  // Compare action row — between confidence bar and footer. Single button
  // whose label follows shared compare-state:
  //   off          → "Compare with another"   (sets this as A)
  //   waitA        → "Set as A"               (sets this as A)
  //   waitB, ≠A    → "Use as B · compare"     (finalises the pair)
  //   waitB, == A  → "Selected as A"          (informational, non-action)
  const compareRow = document.createElement("div");
  compareRow.style.cssText =
    "display:flex;align-items:center;gap:8px;padding:8px 0 2px;";
  const compareBtn = document.createElement("button");
  compareBtn.type = "button";
  const compareStatus = document.createElement("span");
  compareStatus.style.cssText = "font-size:11px;color:#9A9A9A;line-height:1.3;";
  compareRow.appendChild(compareBtn);
  compareRow.appendChild(compareStatus);

  function renderCompareBtn(): void {
    const cs = getCompareState();
    const isA = cs.a?.id === v.id;
    let label: string;
    let disabled = false;
    if (cs.mode === "waitB" && isA) { label = "Selected as A"; disabled = true; }
    else if (cs.mode === "waitB")    { label = "Use as B · compare"; }
    else if (cs.mode === "waitA")    { label = "Set as A"; }
    else                              { label = "Compare with another"; }
    compareBtn.textContent = label;
    compareBtn.disabled = disabled;
    compareBtn.style.cssText = `
      background:${disabled ? "rgba(111,179,122,0.16)" : cs.mode !== "off" ? "rgba(255,211,126,0.22)" : "rgba(255,211,126,0.14)"};
      color:${disabled ? "#9BE3A7" : cs.mode !== "off" ? "#FFE6B0" : "#FFD37E"};
      border:1px solid ${disabled ? "rgba(111,179,122,0.45)" : cs.mode !== "off" ? "rgba(255,211,126,0.75)" : "rgba(255,211,126,0.55)"};
      padding:7px 12px;border-radius:999px;
      font:600 11.5px/1 system-ui;letter-spacing:0.03em;text-transform:uppercase;
      cursor:${disabled ? "default" : "pointer"};
    `;
    // Status text right of the button — makes the shared compare state
    // legible even when the pill is off-screen.
    compareStatus.textContent =
      cs.mode === "waitB" && cs.a
        ? `A selected: ${cs.a.name} · Choose B`
        : cs.mode === "waitA"
        ? "Pick Area A"
        : "";
  }

  compareBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    const cs = getCompareState();
    if (cs.mode === "waitB" && cs.a?.id === v.id) return; // "Selected as A" — noop
    const dataset = getActiveDataset();
    // `compareFromSurface` only needs the dataset when finalising B. When
    // declaring A, null is fine.
    compareFromSurface(v, dataset);
  });

  renderCompareBtn();
  activeCompareUnsubscribe = subscribeCompare(renderCompareBtn);

  // Close must be appended FIRST so position:sticky + float:right keeps it
  // pinned to the top-right of the visible card area while content scrolls.
  card.appendChild(close);
  card.appendChild(header);
  card.appendChild(title);
  card.appendChild(reasons);
  // School-presence row — sits flush under the reasons. Built earlier
  // alongside the density bullet so the wording stays consistent.
  card.appendChild(presence);
  // Safety presence row sits directly under school presence so the
  // two derived signals read as a single block.
  card.appendChild(safetyBox);
  // Tradeoff row appears only when the combination contradicts itself.
  // Sits BEFORE the lens-aware "best for / trade-off" block so that
  // when both render the cross-signal headline reads first.
  if (tradeoffBox) card.appendChild(tradeoffBox);
  if (lensWrap) card.appendChild(lensWrap);
  // Mandatory Schools section under School priority. City-specific:
  //   MEL → public-rank-first list of local schools.
  //   HYD → proximity-based: 2 nearest top schools in the city with km.
  if (priority === "school") {
    card.appendChild(buildSchoolsBlock(v));
  }
  // Resident Pulse — "Today in <suburb>" block. Self-hides when there's
  // nothing to show; appears between the Schools block and the freshness
  // line whenever this suburb has cautionary or positive items in the
  // last 7 days. This is the same data the static landing page renders;
  // surfacing it here means residents who tap their suburb on the map
  // see the same daily-relevant info without leaving the map.
  card.appendChild(buildPulseBlock(v));
  // Truth layer - Place Trust Report from the engine. Self-hiding:
  // renders only for engine-indexed cities (Sydney first) and only
  // when at least one pillar is populated. The curated card works
  // unchanged without it; this block adds evidence, never replaces.
  card.appendChild(buildTruthBlock(v.id, v.name));
  card.appendChild(fresh);
  card.appendChild(compareRow);
  card.appendChild(footer);
  card.appendChild(guideRow);
  document.body.appendChild(card);

  // Subscribe to the global lens. If the user toggles Family/Bachelor/
  // Investor while this card is open, re-render in place so every
  // mode-sensitive surface (badge colour, accent strip, Best for line,
  // Tradeoff line, reason order) updates together.
  activeUnsubscribe = subscribe((m) => {
    if (m === mode) return;
    // Guard against re-entrant dismiss→subscribe loops by unsubscribing
    // every handle before we rebuild — showVerdict() will register a new
    // set of subscriptions for the new card.
    releaseSubscriptions();
    showVerdict(v);
  });

  // Subscribe to the global priority. Same contract as the lens subscription:
  // flipping Budget/School/Commute rebuilds the card so Best-for, Tradeoff,
  // reason order, priority badge, and side-accent all move together.
  activePriorityUnsubscribe = subscribePriority((p) => {
    if (p === priority) return;
    releaseSubscriptions();
    showVerdict(v);
  });

  // Subscribe to the global budget band. Flipping Tight/Moderate/Stretch
  // while a card is open re-renders it so the Good-fit/Stretch/Weak-fit
  // copy, fit chip, and decision anchor all move in lockstep. Mirrors the
  // priority subscription pattern so adding this axis didn't need a new
  // lifecycle shape.
  activeBandUnsubscribe = subscribeBudgetBand((newBand) => {
    if (newBand === band) return;
    releaseSubscriptions();
    showVerdict(v);
  });
  // Bucket-level re-render — catches within-band moves where the band
  // subscriber above no-ops (band unchanged) but the clamped midpoint
  // label needs to refresh.
  activeBucketUnsubscribe = subscribeBudgetBucket(() => {
    releaseSubscriptions();
    showVerdict(v);
  });

  // Esc to dismiss
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      dismissVerdict();
      document.removeEventListener("keydown", onKey);
    }
  };
  document.addEventListener("keydown", onKey);
}

/**
 * Empty-state card for taps far from any seeded verdict.
 * `cityName` is the display label of the active city (e.g., "Hyderabad",
 * "Melbourne"). If omitted, the message stays generic so no stale city name
 * can leak through.
 */
export function showNoVerdict(cityName?: string): void {
  dismissVerdict();
  ensureVerdictMobileStyles();
  const card = document.createElement("div");
  card.id = CARD_ID;
  card.style.cssText = `
    position: fixed;
    left: 50%;
    bottom: 16px;
    transform: translateX(-50%);
    width: min(92vw, 360px);
    background: var(--surface);
    color: #ccc;
    border-radius: 12px;
    padding: 14px 18px;
    font: 13px/1.5 system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
    box-shadow: 0 12px 30px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.06);
    z-index: ${Z.toast};
    text-align: center;
  `;
  card.textContent = cityName
    ? `No verdict for this spot yet. Tap near a glowing marker in ${cityName}.`
    : "No verdict for this spot yet. Tap near a glowing marker nearby.";
  document.body.appendChild(card);

  setTimeout(() => {
    if (document.getElementById(CARD_ID) === card) dismissVerdict();
  }, 2400);
}

// `clamp()` REMOVED — its only consumer was the deleted confidence widget
// (`clamp(v.confidenceScore ?? 0, 0, 100)`). Inline it back if a future
// numeric ever needs bounding here.

/**
 * Mandatory Schools block for the popup when priority === "school".
 *
 * City-specific:
 *   MEL → list of local ranked schools (rank + VIC + kind + level +
 *         year + trend). Public/private both shown with labels.
 *   HYD → top-2 nearest top schools with distance in km + curriculum
 *         (IB / International / CBSE / ICSE / State). No "public zone"
 *         language — proximity is the decision frame here.
 *   No data → explicit "ranking data limited" line (no vague fallback).
 */
function buildSchoolsBlock(v: Verdict): HTMLElement {
  const wrap = document.createElement("div");
  wrap.style.cssText = `
    margin:0 0 10px 0;padding:10px 12px;border-radius:10px;
    background:rgba(159,207,224,0.06);
    border-left:3px solid #9FCFE0;
    display:flex;flex-direction:column;gap:6px;
  `;
  const heading = document.createElement("div");
  heading.textContent = "Schools";
  heading.style.cssText =
    "font-weight:700;font-size:11.5px;letter-spacing:0.05em;text-transform:uppercase;color:#9FCFE0;";
  wrap.appendChild(heading);

  const city = deriveCityFromVerdict(v);

  if (city === "hyd") {
    const verdicts = getActiveDataset() ?? [];
    const summary = proximitySummary(v.id, verdicts);
    const nearest = nearestTopSchools(v.id, verdicts, 2);
    // Summary line sits first so the decision framing comes before the
    // school list.
    const summaryEl = document.createElement("div");
    if (summary.tier === "unknown" || nearest.length === 0) {
      summaryEl.textContent = "School proximity data not available";
      summaryEl.style.cssText =
        "font-size:12px;color:#B8B8B8;line-height:1.4;font-style:italic;";
      wrap.appendChild(summaryEl);
      return wrap;
    }
    summaryEl.textContent =
      summary.withinFive >= 1
        ? `${summary.withinFive} top school${summary.withinFive === 1 ? "" : "s"} within 5 km`
        : `Nearest top school \u2014 ${summary.nearestKm!.toFixed(1)} km away`;
    summaryEl.style.cssText =
      "font-size:12.5px;color:#F1F1F1;line-height:1.4;font-weight:600;";
    wrap.appendChild(summaryEl);

    for (const { school: s, distanceKm } of nearest) {
      const row = document.createElement("div");
      row.style.cssText = "display:flex;flex-direction:column;gap:1px;";
      const headline = document.createElement("div");
      // "• Oakridge International — IB · 2.1 km away" / "· in this suburb"
      // when the school's host suburb is the one the popup is showing.
      const curriculum = s.curriculum ?? "";
      const curriculumPart = curriculum ? `${curriculum} \u00b7 ` : "";
      const distPart =
        distanceKm < 0.3 ? "in this suburb" : `${distanceKm.toFixed(1)} km away`;
      headline.textContent =
        `\u2022 ${s.name} \u2014 ${curriculumPart}${distPart}`;
      headline.style.cssText =
        "font-size:12.5px;color:#F1F1F1;line-height:1.35;font-weight:500;";
      row.appendChild(headline);
      // Rank chip below — sub-line with rank + trend for context.
      const meta = document.createElement("div");
      meta.textContent = `Rank ${s.rank} \u00b7 ${s.year} \u00b7 ${trendLabel(s)}`;
      const trendColor =
        s.trend === "up" ? "#9BE3A7" : s.trend === "down" ? "#F1A0A0" : "#B8B8B8";
      meta.style.cssText = `font-size:11px;color:${trendColor};line-height:1.3;padding-left:14px;letter-spacing:0.02em;`;
      row.appendChild(meta);
      wrap.appendChild(row);
    }
    return wrap;
  }

  // ---- MEL: rank-first per-school list -------------------------------
  if (!hasSchoolRank(v.id)) {
    const note = document.createElement("div");
    note.textContent = SCHOOL_RANK_UNKNOWN_LINE;
    note.style.cssText =
      "font-size:12px;color:#B8B8B8;line-height:1.4;font-style:italic;";
    wrap.appendChild(note);
    return wrap;
  }

  for (const s of getSchoolRanks(v.id, 2)) {
    const row = document.createElement("div");
    row.style.cssText = "display:flex;flex-direction:column;gap:1px;";
    const headline = document.createElement("div");
    headline.textContent =
      `\u2022 ${s.name} \u2014 Rank ${s.rank} (${s.state}) \u00b7 ${kindLabel(s.kind)} \u00b7 ${levelLabel(s.level)}`;
    headline.style.cssText =
      "font-size:12.5px;color:#F1F1F1;line-height:1.35;font-weight:500;";
    const meta = document.createElement("div");
    meta.textContent = `${s.year} \u00b7 ${trendLabel(s)}`;
    const trendColor =
      s.trend === "up" ? "#9BE3A7" : s.trend === "down" ? "#F1A0A0" : "#B8B8B8";
    meta.style.cssText = `font-size:11px;color:${trendColor};line-height:1.3;padding-left:14px;letter-spacing:0.02em;`;
    row.appendChild(headline);
    row.appendChild(meta);
    wrap.appendChild(row);
  }
  return wrap;
}

// `freshnessLine()` REMOVED — it stamped `new Date()` (the wall clock) onto
// every card, so every verdict read "Recent signal · … · <current month>"
// forever regardless of when the data was actually reviewed. Time comes
// from data: the card now renders `reviewStampLine` (compare-card.ts),
// which dates the line from the pricing calibration stamp + the researched
// medians' snapshot year.

/**
 * "median · <source> <year>" — credits the researched median that anchored
 * the figure above it. The bank's source strings end in their own year
 * token ("… + CoreLogic 2025"); strip it so the snapshot year renders once.
 */
function priceProvenanceLine(source: string, year: number): string {
  const short = source.replace(/\s+(?:19|20)\d{2}\s*$/, "").trim();
  return short ? `median · ${short} ${year}` : `median · ${year}`;
}

/**
 * Reorder the existing 3 reasons so the reason most useful to the active lens
 * surfaces first. Pure reordering — no rewriting, no new strings, no schema
 * change. If nothing matches the mode, the original order is preserved.
 */
function reorderReasonsForMode(v: Verdict, mode: PersonaMode): string[] {
  const reasons = (v.reasons || []).slice();
  if (reasons.length < 2) return reasons;

  const keywordsByMode: Record<PersonaMode, string[]> = {
    family: [
      "school", "catchment", "family", "quiet", "leafy", "primary",
      "secondary", "park", "playground", "pram",
    ],
    bachelor: [
      "cafe", "bar", "nightlife", "tram", "metro", "train", "cbd",
      "scene", "walk", "rent",
    ],
    investor: [
      "rent", "rental", "yield", "turnover", "resale", "demand",
      "move", "land-release", "new-build", "tenant", "growth",
    ],
  };

  const keys = keywordsByMode[mode];
  const idx = reasons.findIndex((r) => {
    const lower = r.toLowerCase();
    return keys.some((k) => lower.includes(k));
  });
  if (idx <= 0) return reasons;

  // Bring the matched reason to the front, keep the rest in original order.
  const head = reasons[idx];
  const rest = reasons.filter((_, i) => i !== idx);
  return [head, ...rest];
}

/**
 * One-line decision anchor — the closing nudge that lets the user act today
 * without scrolling for more data. Driven entirely by `verdict` level + the
 * active lens. No new data, no schema, no API.
 *
 * Phrasing rules:
 *   - GREEN: confident "go" framed for the persona
 *   - AMBER: workable, but with the one street-level check the persona cares about
 *   - RED: directive "pass" framed for the persona
 *
 * The opening "If you had to choose today" matches the user-supplied example
 * verbatim so the moment of decision is unmistakable.
 */
function decisionAnchorHTML(
  v: Verdict,
  mode: PersonaMode,
  accentColor: string
): string {
  type Copy = { lead: string; body: string };
  const TABLE: Record<VerdictLevel, Record<PersonaMode, Copy>> = {
    green: {
      family:   { lead: "If you had to choose today", body: "this is a solid family pick." },
      bachelor: { lead: "If you had to choose today", body: "this is a good lifestyle bet." },
      investor: { lead: "If you had to choose today", body: "this is a reasonable buy." },
    },
    amber: {
      family:   { lead: "If you had to choose today", body: "it's workable — walk the school streets first." },
      bachelor: { lead: "If you had to choose today", body: "it's workable — check the weekend noise first." },
      investor: { lead: "If you had to choose today", body: "it's workable — underwrite the yield carefully." },
    },
    red: {
      family:   { lead: "If you had to choose today", body: "keep looking — better-fit pockets exist." },
      bachelor: { lead: "If you had to choose today", body: "pass — find a livelier pocket." },
      investor: { lead: "If you had to choose today", body: "pass — the risk isn't priced in." },
    },
  };
  const c = TABLE[v.verdict][mode];
  return `<span style="color:${accentColor};font-weight:700;">${c.lead},</span> ${c.body}`;
}

// `confidenceBand()` REMOVED. Mapped a 0–100 numeric score onto
// High/Medium/Lower labels with a "still reliable for most cases" style
// qualifier. Sole consumer was the confidence widget under the freshness
// line; both went out together because the dual "verdict colour AND
// confidence band" signal was the source of the conflict users called
// out. Verdict colour now carries the act/hedge/pass decision on its
// own.

/* -------------------------------------------------------------------------- */
/*  Resident Pulse — "Today in <suburb>" block (verdict card surface)         */
/* -------------------------------------------------------------------------- */

/**
 * Returns a sticky "Today in <suburb>" block that hydrates from the
 * shared incidents loader. Mirrors the static-page Pulse panel so the
 * verdict card and the per-suburb landing page show identical data.
 *
 * Hydration model:
 *   - Skeleton renders synchronously so the verdict card never feels
 *     laggy or jumps.
 *   - Real data comes from loadIncidents(city) which is the single
 *     point of truth (already wired for KV-first → live → static
 *     fallback per the enterprise redundancy work).
 *   - The block self-hides when there's no real data AND no incidents
 *     to talk about — the verdict card is precious real estate, no
 *     point shipping an empty "all quiet" panel here.
 */
import { loadIncidents } from "../data/incidents-loader";
import type { CityKey } from "../data/incidents-loader";

function buildPulseBlock(v: Verdict): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "hyperlocal-pulse-block";
  wrap.style.cssText = `
    margin: 0 0 10px 0; padding: 10px 12px; border-radius: 10px;
    background: rgba(255, 214, 128, 0.05);
    border-left: 3px solid #FFD37E;
    display: none;
  `;

  const heading = document.createElement("div");
  heading.style.cssText =
    "font-weight:700;font-size:11.5px;letter-spacing:0.05em;text-transform:uppercase;color:#FFD37E;margin-bottom:6px;";
  heading.textContent = `Today in ${v.name}`;
  wrap.appendChild(heading);

  const list = document.createElement("div");
  list.style.cssText = "display:flex;flex-direction:column;gap:6px;";
  wrap.appendChild(list);

  // Hydrate. Use the existing loader so we get KV-first → static →
  // live fetch fallback for free.
  const city: CityKey = v.id.startsWith("mel-")
    ? "mel"
    : v.id.startsWith("blr-")
    ? "blr"
    : v.id.startsWith("syd-")
    ? "syd"
    : "hyd";

  loadIncidents(city)
    .then((feed) => {
      if (!feed.ready) return; // loader returned the empty sentinel — keep block hidden
      const sevenDaysAgo = Date.now() - 7 * 86_400_000;
      const incidentsForSuburb = feed.deduped.filter((it) => {
        if (it.suburb_id !== v.id) return false;
        const t = Date.parse(it.published_at || "");
        return Number.isFinite(t) && t >= sevenDaysAgo;
      });

      if (incidentsForSuburb.length === 0) {
        // Nothing for THIS suburb in the last 7 days. Don't render the
        // block — the user is already looking at a verdict that covers
        // the same period. An empty pulse here would be visual noise.
        return;
      }

      // Render up to 3 rows (verdict card is space-constrained).
      // Sort: videos first (eye-catchy), then by age. Helps the user
      // immediately see if there's a watchable clip vs only news links.
      const sorted = [...incidentsForSuburb].sort((a, b) => {
        const av = (a as { type?: string }).type === "video" ? 0 : 1;
        const bv = (b as { type?: string }).type === "video" ? 0 : 1;
        if (av !== bv) return av - bv;
        const at = Date.parse(a.published_at || "") || 0;
        const bt = Date.parse(b.published_at || "") || 0;
        return bt - at;
      });
      const rows = sorted.slice(0, 3);
      rows.forEach((it) => {
        const isVideo = (it as { type?: string }).type === "video";
        const channel = (it as { channel?: string }).channel || it.source || "";

        const row = document.createElement("a");
        row.href = it.url || "#";
        if (it.url) {
          row.target = "_blank";
          row.rel = "noopener nofollow";
        }
        // Different visual tier for videos: subtle red-700 dot + ▶ glyph
        // so the user reads "watchable" at a glance without ambiguity.
        const cat = isVideo
          ? "#E5484D"
          : it.category === "cautionary"
            ? "#F1A11B"
            : "#3FB950";
        row.style.cssText = `
          display:flex;gap:8px;align-items:flex-start;
          font-size:12px;line-height:1.4;color:#F4F2EE;
          text-decoration:none;padding:4px 0;
        `;
        if (isVideo) {
          row.setAttribute(
            "aria-label",
            `Video · ${channel} · ${it.headline || ""}`
          );
        }

        const indicator = document.createElement("span");
        if (isVideo) {
          // Triangle play glyph in a small disc — instantly readable as
          // "watch" without needing a thumbnail (which would crowd the
          // verdict card). Same colour as the YouTube-red bubble badge.
          indicator.style.cssText = `
            width:14px;height:14px;border-radius:3px;flex-shrink:0;
            background:${cat};margin-top:2px;
            display:inline-flex;align-items:center;justify-content:center;
            color:#fff;font-size:9px;line-height:1;
          `;
          indicator.textContent = "▶";
          indicator.setAttribute("aria-hidden", "true");
        } else {
          indicator.style.cssText = `
            width:8px;height:8px;border-radius:50%;flex-shrink:0;
            background:${cat};margin-top:5px;
          `;
        }
        row.appendChild(indicator);

        const text = document.createElement("span");
        // One voice for time: freshness.ts owns relative-age phrasing. An
        // unparseable timestamp is said as unverified, never aged from NaN.
        const publishedTs = Date.parse(it.published_at || "");
        const ageLabel = Number.isFinite(publishedTs)
          ? relativeAge(publishedTs, Date.now())
          : "time unverified";
        // Channel name only for videos — gives the user a fast sense of
        // who's behind the watchable clip vs another text article.
        const channelTag = isVideo && channel
          ? `<span style="color:#FFD37E;font-weight:500;"> ${escapeForCard(channel)}</span> · `
          : "";
        text.innerHTML = `${channelTag}${escapeForCard(it.headline || "")} <span style="color:#888;">· ${ageLabel}</span>`;
        row.appendChild(text);

        list.appendChild(row);
      });

      // If more than 3, show a tail link
      if (incidentsForSuburb.length > 3) {
        const tail = document.createElement("div");
        tail.style.cssText =
          "font-size:11px;color:#888;margin-top:4px;font-style:italic;";
        tail.textContent = `+ ${incidentsForSuburb.length - 3} more this week`;
        list.appendChild(tail);
      }

      // Reveal the block now that we have content.
      wrap.style.display = "block";
    })
    .catch(() => {
      // Loader failed; keep block hidden. The static page Pulse panel
      // serves the same suburb so the user has a fallback.
    });

  return wrap;
}

function escapeForCard(s: string): string {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
